import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AdminServices from "./AdminServices";
import Loader from "../common/loader";

const PasswordResetModal = ({ open, onClose, prefillEmail = '' }) => {
    const [formData, setFormData] = useState({
        email: prefillEmail,
        newPassword: '',
        confirmPassword: '',
        mustChangePassword: false,
    });

    // Update email when prefillEmail changes
    useEffect(() => {
        if (prefillEmail) {
            setFormData(prev => ({ ...prev, email: prefillEmail }));
        }
    }, [prefillEmail]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const validateForm = () => {
        const newErrors = {};

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Password validation
        if (!formData.newPassword.trim()) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long';
        }

        // Confirm password validation
        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Please confirm the new password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrorMessage('');

        try {
            const response = await AdminServices.resetUserPassword({
                email: formData.email.trim(),
                newPassword: formData.newPassword.trim(),
                mustChangePassword: formData.mustChangePassword === true,
            });

            if (response.success) {
                setSuccess(true);
                // Auto-close modal after 2 seconds
                setTimeout(() => {
                    handleClose();
                }, 2000);
            } else {
                setErrorMessage(response.message || 'Failed to reset password');
            }
        } catch (error) {
            console.error('Password reset error:', error);
            setErrorMessage(
                error.response?.data?.message ||
                error.message ||
                'An error occurred while resetting the password'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset form state (but preserve prefillEmail if provided)
        setFormData({
            email: prefillEmail,
            newPassword: '',
            confirmPassword: '',
            mustChangePassword: false,
        });
        setErrors({});
        setSuccess(false);
        setErrorMessage('');
        setLoading(false);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-lock me-2"></i>
                            {prefillEmail ? 'Change Password' : 'Reset Password'}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleClose}
                            disabled={loading}
                        ></button>
                    </div>

                    <div className="modal-body">
                        <Loader loading={loading} />

                        {success ? (
                            <div className="text-center py-3">
                                <div className="alert alert-success">
                                    <i className="fas fa-check-circle me-2"></i>
                                    {prefillEmail ? 'Password changed successfully!' : 'Password updated successfully!'}
                                </div>
                                <p className="text-muted">
                                    {prefillEmail 
                                        ? 'Your password has been changed successfully. Please log in again with your new password.'
                                        : 'The user can log in with this new password immediately.'
                                    }
                                </p>
                            </div>
                        ) : (
                            <div>
                                {errorMessage && (
                                    <div className="alert alert-danger alert-dismissible fade show">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {errorMessage}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setErrorMessage('')}
                                        ></button>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label htmlFor="email" className="form-label">Email</label>
                                    <input
                                        type="email"
                                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                        id="email"
                                        value={formData.email}
                                        onChange={handleInputChange('email')}
                                        placeholder={prefillEmail ? "Your email address" : "Enter email address"}
                                        disabled={loading || !!prefillEmail}
                                        readOnly={!!prefillEmail}
                                    />
                                    {errors.email && (
                                        <div className="invalid-feedback">{errors.email}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="newPassword" className="form-label">New Password</label>
                                    <input
                                        type="password"
                                        className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                                        id="newPassword"
                                        value={formData.newPassword}
                                        onChange={handleInputChange('newPassword')}
                                        placeholder="Enter new password (min 8 characters)"
                                        disabled={loading}
                                    />
                                    {errors.newPassword && (
                                        <div className="invalid-feedback">{errors.newPassword}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                                    <input
                                        type="password"
                                        className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                        id="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange('confirmPassword')}
                                        placeholder="Confirm the new password"
                                        disabled={loading}
                                    />
                                    {errors.confirmPassword && (
                                        <div className="invalid-feedback">{errors.confirmPassword}</div>
                                    )}
                                </div>

                                <div className="form-check mb-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="mustChangePassword"
                                        checked={formData.mustChangePassword === true}
                                        onChange={(e) => {
                                            const checked = e.target.checked === true;
                                            setFormData(prev => ({ ...prev, mustChangePassword: checked }));
                                        }}
                                        disabled={loading}
                                    />
                                    <label className="form-check-label" htmlFor="mustChangePassword">
                                        Force user to reset password on next login
                                    </label>
                                    <div className="form-text">
                                        Default is <strong>do not force</strong>. If enabled, the user will be blocked from completing login until they set a new password.
                                    </div>
                                </div>

                                {!prefillEmail && (
                                    <div className="alert alert-info">
                                        <i className="fas fa-info-circle me-2"></i>
                                        <strong>Note:</strong> This sets the user&apos;s password immediately. This action does not automatically email the user.
                                    </div>
                                )}
                                {prefillEmail && (
                                    <div className="alert alert-info">
                                        <i className="fas fa-info-circle me-2"></i>
                                        <strong>Note:</strong> After changing your password, you will need to log in again with your new password.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {!success && (
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Resetting...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-lock me-2"></i>
                                        {prefillEmail ? 'Change Password' : 'Reset Password'}
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

PasswordResetModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    prefillEmail: PropTypes.string
};

export default PasswordResetModal;
